import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";

import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";

const ManageManualTokensPage = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ userId: "", amount: "", tokens: "", note: "" });
  const [basePrice, setBasePrice] = useState(1);
  const [clubs, setClubs] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);

  useEffect(() => {
    fetchRecords();
    fetchTokenRate();
  }, []);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/manual-token-credits`);
      const data = await res.json();
      setRecords(data);
    } catch (err) {
      console.error("Error fetching manual token credits:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTokenRate = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/token/metrics`);
      const data = await res.json();
      setBasePrice(data.base_price || 1);
    } catch (err) {
      console.error("Error fetching token metrics:", err);
    }
  };

  const fetchClubsAndSponsors = async () => {
    try {
      const [clubsRes, sponsorsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/clubs`),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/sponsors`)
      ]);

      const [clubsData, sponsorsData] = await Promise.all([
        clubsRes.json(),
        sponsorsRes.json()
      ]);

      const clubOptions = clubsData.map(club => ({
        value: `club-${club.id}`,
        label: `Club - ${club.name}`,
      }));

      const sponsorOptions = sponsorsData.map(sponsor => ({
        value: `sponsor-${sponsor.id}`,
        label: `sponsor - ${sponsor.name}`,
      }));

      setClubs([...clubOptions, ...sponsorOptions]);
    } catch (err) {
      console.error("Error fetching clubs or sponsors:", err);
    }
  };

  const openForm = () => {
    fetchClubsAndSponsors();
    setFormData({ userId: "", amount: "", tokens: "", note: "" });
    setWalletBalance(0);
    setShowForm(true);
  };

  const fetchWalletBalance = async (userId) => {
    if (!userId) {
      setWalletBalance(0);
      return;
    }

    const extractedId = parseInt(userId.split("-")[1]);
    if (isNaN(extractedId)) {
      setWalletBalance(0);
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/get-wallet-balance/${extractedId}`);
      const data = await res.json();
      setWalletBalance(parseFloat(data.wallet_balance || 0));
    } catch (err) {
      console.error("Error fetching wallet balance:", err);
      setWalletBalance(0);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedForm = { ...formData, [name]: value };

    if (name === "amount") {
      const amount = parseFloat(value) || 0;
      updatedForm.amount = value;

      const maxAllowedEuro = amount * 0.07;
      const maxAllowedTokens = maxAllowedEuro / basePrice;

      const tokensToUse = Math.min(walletBalance || Infinity, maxAllowedTokens);
      updatedForm.tokens = Math.round(tokensToUse);
    }

    setFormData(updatedForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const extractedId = formData.userId?.includes("-")
      ? parseInt(formData.userId.split("-")[1])
      : null;

    const submitData = {
      ...formData,
      userId: extractedId,
    };

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/manual-token-credits`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(submitData),
      });

      if (res.ok) {
        fetchRecords();
        setShowForm(false);
        setFormData({ userId: "", amount: "", tokens: "", note: "" });
        toast.success("Manual token credit recorded successfully.", { position: "top-right" });
      } else {
        const errorData = await res.json();
        toast.error(errorData.message || "Unexpected server error. Please contact support.", { position: "top-right" });
      }
    } catch (err) {
      console.error("Error submitting:", err);
      toast.error("Unable to reach server. Please check your connection and try again.", { position: "top-right" });
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "User/Account",
        accessorKey: "user_name",
        cell: info => info.getValue() || "Admin Account",
      },
      {
        header: "Amount (EUR)",
        accessorKey: "amount_eur",
      },
      {
        header: "Tokens Credited",
        accessorKey: "tokens_credited",
      },
      {
        header: "Note",
        accessorKey: "note",
      },
      {
        header: "Created At",
        accessorKey: "created_at",
        cell: info => new Date(info.getValue()).toLocaleDateString(),
      },
    ],
    []
  );

  const table = useReactTable({
    data: records,
    columns,
    state: { globalFilter },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setGlobalFilter,
  });

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Manual Token Credits Overview</h1>

      <button
        onClick={openForm}
        className="mb-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
      >
        Add New Credit
      </button>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search..."
              className="border p-2 rounded w-full sm:w-1/2"
              value={globalFilter ?? ""}
              onChange={e => setGlobalFilter(e.target.value)}
            />
          </div>

          <div className="overflow-x-auto border rounded mb-6">
            <table className="min-w-full table-auto text-sm text-left text-gray-700">
              <thead className="bg-gray-100">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                        className="p-3 font-semibold cursor-pointer select-none text-left"
                      >
                        <div className="flex items-center gap-1">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: "▲",
                            desc: "▼",
                          }[header.column.getIsSorted()] ?? ""}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id} className="border-t hover:bg-gray-100 even:bg-gray-50">
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="p-3 text-left">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 relative">
            <h2 className="text-xl font-bold mb-4">Add Manual Credit</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Select
                className="w-full"
                options={[
                  { value: "", label: "Admin Account (default)" },
                  ...clubs
                ]}
                value={
                  formData.userId
                    ? clubs.find(opt => opt.value === formData.userId) || null
                    : { value: "", label: "Admin Account (default)" }
                }
                onChange={(selected) => {
                  const userId = selected.value;
                  setFormData({
                    userId,
                    amount: "",
                    tokens: "",
                    note: formData.note,
                  });
                  fetchWalletBalance(userId);
                }}
              />

              {formData.userId && (
                <div className="text-sm text-gray-600">
                  Current Token Balance: <span className="font-semibold">{walletBalance.toLocaleString()}</span>
                </div>
              )}
              <input
                type="number"
                name="amount"
                placeholder="Amount (€)"
                value={formData.amount}
                onChange={handleInputChange}
                className="w-full border p-2 rounded"
                required
              />
              <input
                type="text"
                name="tokens"
                placeholder="Tokens"
                value={formData.tokens}
                readOnly
                className="w-full border p-2 rounded bg-gray-100"
              />
              <textarea
                name="note"
                placeholder="Note (reference/invoice, remarks)"
                value={formData.note}
                onChange={handleInputChange}
                className="w-full border p-2 rounded"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-1 rounded bg-gray-300 hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                >
                  Submit
                </button>
              </div>
            </form>

            <button
              onClick={() => setShowForm(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-xl"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageManualTokensPage;
